(ns frontend.ui.page.lp-report-editor.step-1-initialization.card.fund-transfer-file
  (:require
   [frontend.ui.page.lp-report-editor.common.view :as common-view]
   [frontend.ui.component.card.view :as card]
   [common.localization :as loc]
   [frontend.ui.page.lp-report-editor.step-1-initialization.sub :as sub]
   [frontend.ui.page.lp-report-editor.step-1-initialization.event :as event]
   [frontend.ui.page.lp-report-editor.event :as editor-event]
   [frontend.ui.page.lp-report-editor.sub :as editor-sub]
   [frontend.ui.component.file-input.view :as file-input]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [herb.core :refer [<class]]
   [frontend.ui.component.grid.view :as grid]
   [frontend.ui.component.file-input.style :as file-input-style]
   [frontend.ui.component.icon.view :as icon-view]
   [frontend.ui.page.lp-report-editor.common.style :as common-style]
   [frontend.utils :as fu]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.expansion-panel.view :as expansion-panel]
   [common.lp-portal-import-errors :as import-errors]
   [common.util :as cu]))

(defn- formula-error-list
  "One row per sheet/function group."
  [formula-errors]
  [:ul
   {:class-name "list-disc pl-5 mt-1"}
   (for [{:excel/keys [sheet function] :as group} formula-errors]
     ^{:key (str sheet "!" function)}
     [:li (import-errors/group-description group)])])

(defn- import-error-alert
  "Failure detail, rendered below the file row."
  [{:keys [error-detail formula-errors]}]
  (when error-detail
    [:div
     {:class-name "mt-2"}
     [alert/danger {}
      (if (seq formula-errors)
        (let [total (import-errors/total-error-count formula-errors)]
          [:<>
           [:div (if (= 1 total)
                   (loc/tr :lp-import-error-formula-intro-one)
                   (loc/tr :lp-import-error-formula-intro [total]))]
           [formula-error-list formula-errors]
           [:div
            {:class-name "mt-2"}
            (loc/tr :lp-import-error-formula-advice)]])
        [:div error-detail])]]))

(defn- import-history-row
  [{:keys [at file-id file-name outcome] :as attempt} report-id]
  (let [failed? (= outcome :processing.status/error)
        detail (import-errors/attempt-detail attempt)]
    [:div
     {:class-name "py-2 border-b border-solid border-base-light-border-paler last:border-b-0"}
     [:div
      {:class-name "flex items-baseline gap-3 text-sm"}
      [:span
       ;; Fixed width so the filenames line up regardless of the label length.
       {:class-name (str "shrink-0 w-20 font-bold "
                         (if failed?
                           "text-danger-light-text-accent"
                           "text-success-light-text-accent"))}
       (loc/tr (if failed?
                 :processing.status/error
                 :success))]
      [:span
       {:class-name "truncate"
        :title      file-name}
       file-name]
      (when file-id
        [:span
         {:class-name "shrink-0"}
         [icon-view/icon
          {:on-click #(rf/dispatch [::editor-event/download-data-file report-id file-id])}
          :download]])
      [:span {:class-name "flex-1 text-right shrink-0 opacity-60"}
       (fu/display-datetime-timestamp at)]]
     (when detail
       [:div {:class-name "text-xs mt-1 opacity-75"} detail])]))

(defn- import-history
  "Past import attempts, newest first. Collapsed by default and scrollable, so
  a report with a long upload history does not push the rest of the card down."
  [_report-id]
  (let [local-state (r/atom {:expanded? false})]
    (fn [report-id]
      (let [attempts @(rf/subscribe [::editor-sub/import-history])]
        (when (seq attempts)
          [:div
           {:class-name "mt-4"}
           [expansion-panel/view
            {:label        (str (loc/tr :lp-import-history-title)
                                " ("
                                (count attempts)
                                ")")
             :expanded?    (:expanded? @local-state)
             :on-expand!   #(swap! local-state assoc :expanded? true)
             :on-collapse! #(swap! local-state assoc :expanded? false)}
            [:div
             ;; pr-3 keeps the row content clear of the scrollbar.
             {:class-name "max-h-64 overflow-y-auto font-normal pr-3"}
             (for [{:keys [at] :as attempt} attempts]
               ^{:key at}
               [import-history-row attempt report-id])]]])))))

(defn- fund-data-file-status
  [{:keys [report-id file-id file-name status progress message
           error-detail formula-errors]}]
  [:<>
   [:div
    {:class (<class file-input-style/file-status)}
    [grid/row {}
     [grid/column
      {:span 6}
      [:div
       {:class (<class file-input-style/file-name)}
       [:div
        {:class (<class file-input-style/file-name-icon)}
        [icon-view/icon :paperclip]]
       file-name]]
     [grid/column {:span 2}
      [:div
       {:class (<class file-input-style/file-name)}
       (loc/tr status)]]
     [grid/column {:span 3}
      [:div
       {:class (<class file-input-style/progress-bar-container)}
       ;; Label over the track, not inside the fill.
       [:div
        {:class (<class file-input-style/progress-bar progress)}]
       [:span
        {:class (<class file-input-style/progress-bar-description)}
        message]]]
     [grid/column {:span 1}
      [:div
       {:class-name "flex items-center gap-3"}
       ;; Most useful after a failure: the file is needed to fix the formulas.
       (when file-id
         [icon-view/icon
          {:on-click #(rf/dispatch [::editor-event/download-data-file report-id file-id])}
          :download])
       [:a
        {:href     "#"
         :class    (<class file-input-style/file-remove)
         :on-click #(rf/dispatch [::event/remove-fund-config-file report-id file-id])}
        [icon-view/icon :trash-alt]]]]]
    [import-error-alert
     {:error-detail   error-detail
      :formula-errors formula-errors}]]])

(defn- read-mode
  [{:keys [file-id file-name status]}]
  (let [report @(rf/subscribe [::editor-sub/lp-report])]
    (if (or (not (:lp-report/template-type report))
            (cu/blank-value? file-name))
      [grid/row {}
      ]
      [grid/row {}
       [grid/column {}
        [:div
         {:class (<class file-input-style/file-name)}
         [icon-view/icon
          {:class (<class file-input-style/file-name-icon)}
          :paperclip]
         file-name]]
       [grid/column {}]])))

(defn footer
  []
  [:div
   {:class (<class common-style/footer-right)}
   [:div
    {:class (<class common-style/spacing-top)}
    [button/secondary-hollow
     {:on-click (fn [e]
                  (fu/prevent-default-event! e)
                  (rf/dispatch [::event/close-edit-mode :step-2]))}
     (loc/tr :close)]]])

(defn tools
  [edit-mode?]
  (let [can-edit? @(rf/subscribe [::sub/can-edit? :step-2])
        collapsed? @(rf/subscribe [::sub/collapsed? :step-2])]
    (concat []
            (when (and (not edit-mode?)
                       can-edit?)
              [[icon-view/icon
                {:class    (<class (fn []
                                     {:margin-right "6px"}))
                 :on-click (fn [e]
                             (fu/prevent-default-event! e)
                             (rf/dispatch [::event/open-edit-mode :step-2]))}
                :edit]])
            (when-not edit-mode?
              [[icon-view/icon
                {:on-click (fn [e]
                             (fu/prevent-default-event! e)
                             (rf/dispatch [::event/toggle-collapse :step-2]))}
                (if collapsed?
                  :angle-down
                  :angle-up)]]))))

(defn view
  []
  (let [{:keys [file-id file-name status progress message
                error-detail formula-errors]} @(rf/subscribe [::sub/fund-file-status])
        report @(rf/subscribe [::editor-sub/lp-report])]
    (let [collapsed? @(rf/subscribe [::sub/collapsed? :step-2])
          edit-mode? @(rf/subscribe [::sub/edit-mode? :step-2])
          step-disabled? @(rf/subscribe [::sub/step-disabled? :step-2])
          step-completed? @(rf/subscribe [::sub/step-complete? :step-2])
          report @(rf/subscribe [::editor-sub/lp-report])]
      [card/card-with-tools
       {:collapsed?       (or step-disabled? collapsed?)
        :disabled?        step-disabled?
        :toggle-collapsed #(rf/dispatch [::event/toggle-collapse :step-2])
        :icon             (if step-completed?
                            [common-view/icon-step-done]
                            [common-view/icon-step])
        :title            (loc/tr :lp-report-editor-step-2-title)
        :tool-icons       (when-not step-disabled?
                            (tools edit-mode?))}
       (cond
         (not edit-mode?)
         [grid/row {}
          [grid/column {:span 12}
           [read-mode
            {:report-id (:db/id report)
             :file-id   file-id
             :file-name file-name
             :status    status}]]]

         (= status :status/no-file)
         [file-input/file-input
          {:input-id        :fund-file
           :handle-add-file #(rf/dispatch [::event/start-fund-file-upload %])}]

         (= status :status/uploading)
         [grid/row {}
          [grid/column {:span 12}
           [file-input/file-upload-status
            {:handle-remove-file #(rf/dispatch [::event/remove-fund-config-file (:db/id report)
                                                nil])}
            {:filename file-name
             :percents progress
             :status   status
             :message  message}]]]

         :else
         [grid/row {}
          [grid/column {:span 12}
           [fund-data-file-status
            {:report-id      (:db/id report)
             :file-id        file-id
             :file-name      file-name
             :progress       progress
             :message        message
             :status         status
             :error-detail   error-detail
             :formula-errors formula-errors}]]])
       ;; Read-only, so it shows outside edit mode too - reviewing a failed
       ;; import is exactly when it is wanted.
       [grid/row {}
        [grid/column {:span 12}
         [import-history (:db/id report)]]]
       (when edit-mode?
         [footer])])))
